import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { STEPS } from "@/components/wizard/Steps";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Loader2, Save } from "lucide-react";

export default function WillForm() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get("/will");
        setData(res.will?.data || {});
        setSubmitted(!!res.progress?.submitted);
      } catch (e) { setData({}); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load draft once on mount

  const patch = (section, obj) => setData((d) => ({ ...d, [section]: { ...(d[section] || {}), ...obj } }));
  const setField = (key, value) => setData((d) => ({ ...d, [key]: value }));
  const setList = (key, arr) => setData((d) => ({ ...d, [key]: arr }));

  const persist = async (completed = false) => {
    const { data: res } = await api.post("/will/save", { data, completed });
    return res;
  };

  const next = async () => {
    setSaving(true);
    try {
      await persist(false);
      if (current < STEPS.length - 1) { setCurrent((c) => c + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const back = () => { if (current > 0) { setCurrent((c) => c - 1); window.scrollTo({ top: 0, behavior: "smooth" }); } };

  const saveDraft = async () => {
    setSaving(true);
    try { await persist(false); toast.success("Draft saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const complete = async () => {
    if (!data?.service?.package) { toast.error("Please select a service to continue"); return; }
    setSaving(true);
    try {
      await persist(true);
      toast.success("Form completed — continue to payment");
      navigate("/pay");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const StepComp = useMemo(() => STEPS[current].Component, [current]);

  if (data === null)
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="w-8 h-8 animate-spin text-brand-green" /></div>;

  const isLast = current === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <SiteHeader />
      <div className="max-w-6xl w-full mx-auto px-5 md:px-8 py-8 md:py-12 grid md:grid-cols-3 gap-8">
        {/* Progress tracker */}
        <aside className="md:col-span-1">
          <div className="md:sticky md:top-24">
            <h2 className="font-serif text-2xl text-brand-green mb-1">Will Instruction</h2>
            <p className="text-sm text-brand-muted mb-6">Complete each section. Your progress is saved automatically.</p>
            <ol className="relative border-l border-brand-line ml-3 space-y-1">
              {STEPS.map((s, i) => {
                const done = i < current, active = i === current;
                return (
                  <li key={s.key} className="relative pl-6 py-2 cursor-pointer" onClick={() => setCurrent(i)} data-testid={`step-nav-${s.key}`}>
                    <span className={`absolute -left-[9px] top-3 w-4 h-4 rounded-full flex items-center justify-center border transition-colors
                      ${active ? "bg-brand-green border-brand-green" : done ? "bg-brand-gold border-brand-gold" : "bg-white border-brand-line"}`}>
                      {done && <Check className="w-2.5 h-2.5 text-white" />}
                    </span>
                    <span className={`text-sm transition-colors ${active ? "font-semibold text-brand-green" : done ? "text-brand-ink" : "text-brand-muted"}`}>
                      {s.label}
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </aside>

        {/* Form card */}
        <main className="md:col-span-2">
          {submitted && (
            <div className="mb-5 rounded-lg border border-brand-success/30 bg-brand-success/10 text-brand-success text-sm px-4 py-3">
              This instruction has already been submitted. Any edits will not change the submitted copy — please contact us for changes.
            </div>
          )}
          <div className="bg-white rounded-xl border border-brand-line p-6 md:p-10 shadow-sm animate-fade-up" key={current}>
            <div className="text-xs font-semibold text-brand-muted mb-4">Step {current + 1} of {STEPS.length}</div>
            <StepComp data={data} patch={patch} setField={setField} setList={setList} />
          </div>

          <div className="flex items-center justify-between mt-6">
            <Button variant="outline" onClick={back} disabled={current === 0} data-testid="wizard-back"
              className="border-brand-line text-brand-green disabled:opacity-40">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={saveDraft} disabled={saving} className="text-brand-muted" data-testid="wizard-save-draft">
                <Save className="w-4 h-4 mr-1.5" /> Save draft
              </Button>
              {isLast ? (
                <Button onClick={complete} disabled={saving} className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="wizard-complete">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Complete & pay
                </Button>
              ) : (
                <Button onClick={next} disabled={saving} className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="wizard-next">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save & continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
